import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Check, Search, Building2, RefreshCw, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { validarRutChileno } from '../lib/rut';
// Iter 307.b (Carlos): MISMO formulario que "Información > Personas > Clientes
// > Nuevo Cliente" — misma información requerida, mismo registro (POST /clientes).
import { ClienteFormFields, CLIENTE_INITIAL_FORM } from './ClienteFormFields';

/**
 * ClienteAlegraSelect — selector de CLIENTES para Proyectos/OTs.
 *
 * Iter 232 (Carlos): los proyectos usan clientes con RUT válido para que viaje
 * a OTs, proformas y facturas.
 * Iter 307 (Carlos, jun-2026): el selector combina clientes de Alegra + clientes
 * creados en el ERP (disponibles AL INSTANTE). Si el cliente no existe, se crea
 * aquí mismo con las restricciones del módulo Clientes (RUT módulo 11, formato
 * 00000000-A). El contacto se sube a Alegra en la próxima sincronización.
 *
 * Props:
 *   value: { alegra_id, cliente_id?, nombre, rut } | null
 *   onChange: ({ alegra_id, cliente_id, nombre, rut }) => void
 *   placeholder?: string
 */
export default function ClienteAlegraSelect({ value, onChange, placeholder = "Buscar cliente (nombre o RUT)..." }) {
  const [search, setSearch] = useState(value?.nombre || '');
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const containerRef = useRef(null);

  // Iter 307 — diálogo de creación de cliente (formulario completo del maestro Clientes)
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCli, setNewCli] = useState(CLIENTE_INITIAL_FORM);
  const [condicionesPago, setCondicionesPago] = useState([]);

  useEffect(() => {
    if (createOpen && condicionesPago.length === 0) {
      api.get('/condiciones-pago-crud', { params: { solo_nuevas: true } })
        .then((r) => setCondicionesPago(r.data || []))
        .catch(() => setCondicionesPago([]));
    }
  }, [createOpen, condicionesPago.length]);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await api.get(`/proyectos-alegra/clientes-search${q ? '?q=' + encodeURIComponent(q) : ''}`);
      setOptions(res.data?.items || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);
  useEffect(() => { setSearch(value?.nombre || ''); }, [value]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { if (showDropdown) load(search); }, 250);
    return () => clearTimeout(t);
  }, [search, showDropdown, load]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (item) => {
    setSearch(item.nombre);
    onChange?.({
      alegra_id: String(item.alegra_id || ''),
      cliente_id: item.cliente_id || '',
      nombre: item.nombre || '',
      rut: item.identificacion || '',
    });
    setShowDropdown(false);
  };

  const abrirCrearCliente = () => {
    setNewCli({ ...CLIENTE_INITIAL_FORM, razon_social: search.trim() });
    setShowDropdown(false);
    setCreateOpen(true);
  };

  const crearCliente = async () => {
    // Mismas validaciones que "Nuevo Cliente" en el maestro (ClientesList)
    if (!newCli.razon_social.trim()) { toast.error('La razón social es obligatoria'); return; }
    if (newCli.rut && newCli.rut.trim() && !validarRutChileno(newCli.rut)) {
      toast.error('RUT inválido: revisa el dígito verificador (módulo 11)');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/clientes', { ...newCli, razon_social: newCli.razon_social.trim() });
      const c = res.data;
      toast.success(`Cliente creado: ${c.razon_social}`);
      if (c.alegra_sync_pendiente) {
        toast.info('Se subirá a Alegra en la próxima sincronización. Ya está disponible en el ERP.');
      }
      select({
        alegra_id: c.alegra_id || '',
        cliente_id: c.id,
        nombre: c.razon_social,
        identificacion: c.rut,
      });
      setCreateOpen(false);
      load('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear el cliente');
    } finally {
      setCreating(false);
    }
  };

  const refreshContact = async () => {
    if (!value?.alegra_id && !value?.rut) return;
    setRefreshing(true);
    try {
      const res = await api.post('/alegra/contacto/refresh', {
        alegra_id: value?.alegra_id || undefined,
        rut: value?.rut || undefined,
      });
      const c = res.data?.contact;
      if (c) {
        toast.success(`Contacto refrescado: ${c.nombre}`);
        onChange?.({
          alegra_id: String(c.alegra_id || value.alegra_id),
          cliente_id: value?.cliente_id || '',
          nombre: c.nombre || value.nombre,
          rut: c.identificacion || value.rut,
        });
        load(search);
      } else {
        toast.error('No se obtuvieron datos del contacto');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`Refresh falló: ${detail}`);
    } finally {
      setRefreshing(false);
    }
  };

  const seleccionado = Boolean(value?.alegra_id || value?.cliente_id);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          data-testid="cliente-alegra-search"
        />
        {seleccionado && value?.nombre === search && (
          <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="cliente-alegra-dropdown">
          {loading && (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">Buscando...</div>
          )}
          {!loading && options.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              {search.trim() ? (
                <>
                  Sin coincidencias.
                  <br />
                  <button
                    type="button"
                    onClick={abrirCrearCliente}
                    className="mt-1 text-xs text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="cliente-crear-desde-vacio"
                  >
                    <Plus className="h-3 w-3" /> Crear cliente nuevo "{search.trim()}"
                  </button>
                </>
              ) : 'Escribe para buscar...'}
            </div>
          )}
          {!loading && options.slice(0, 50).map((c) => (
            <button
              key={`${c.alegra_id || ''}-${c.cliente_id || ''}`}
              type="button"
              className={`w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center gap-2 ${
                seleccionado && String(c.alegra_id || '') === String(value?.alegra_id || '') && (c.alegra_id || c.cliente_id === value?.cliente_id) ? 'bg-primary/10 text-primary font-medium' : ''
              }`}
              onClick={() => select(c)}
              data-testid={`cliente-alegra-option-${c.alegra_id || c.cliente_id}`}
            >
              <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate">{c.nombre}</div>
                {c.identificacion && (
                  <div className="text-[10px] text-muted-foreground tabular-nums">RUT: {c.identificacion}</div>
                )}
              </div>
              {c.fuente === 'erp' && !c.alegra_id && (
                <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-200" title="Creado en el ERP — se subirá a Alegra en la próxima sincronización">
                  Pendiente Alegra
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {seleccionado && value?.rut && (
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            <span className="font-mono">RUT: {value.rut}</span>
            {value?.alegra_id
              ? <> · Alegra ID: {value.alegra_id}</>
              : <> · <span className="text-amber-600 font-medium">se subirá a Alegra en la próxima sincronización</span></>}
          </p>
          {value?.alegra_id && (
            <button
              type="button"
              onClick={refreshContact}
              disabled={refreshing}
              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait"
              data-testid="cliente-alegra-refresh"
              title="Sincroniza este contacto desde Alegra ahora (sin esperar al cron horario)"
            >
              <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refrescando...' : 'Refrescar desde Alegra'}
            </button>
          )}
        </div>
      )}

      {/* Iter 307 (Carlos): el cliente se crea en el ERP, no en Alegra. */}
      <div className="mt-1 flex justify-end">
        <button
          type="button"
          onClick={abrirCrearCliente}
          className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
          data-testid="cliente-erp-crear-btn"
        >
          <Plus className="h-3 w-3" /> Crear cliente nuevo
        </button>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-crear-cliente">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
            <DialogDescription>
              Mismo formulario del maestro de Clientes — se guarda en el mismo registro y queda disponible de inmediato. El contacto se sube a Alegra en la próxima sincronización.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ClienteFormFields
              formData={newCli}
              setFormData={setNewCli}
              condicionesPago={condicionesPago}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating} data-testid="nuevo-cliente-cancelar">
              Cancelar
            </Button>
            <Button onClick={crearCliente} disabled={creating} data-testid="nuevo-cliente-guardar">
              {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Crear cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
